import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  memo,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../Components/Short.css';

/* ──────────────────────  UTILITIES  ────────────────────── */
const throttle = (func, limit) => {
  let lastFunc, lastRan;
  return (...args) => {
    if (!lastRan) {
      func(...args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func(...args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

const debounce = (func, delay) => {
  let tid;
  return (...args) => {
    clearTimeout(tid);
    tid = setTimeout(() => func(...args), delay);
  };
};

/* ──────────────────────  SHORT ITEM WITH LAZY RENDERING  ────────────────────── */
const ShortItem = memo(
  ({ videoId, index, isVisible, onLoad, onProgressClick, onProgressDragStart }) => {
    const videoRef = useRef(null);
    const progressRef = useRef(null);
    const progressBarRef = useRef(null);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
      if (isVisible && !shouldRender) {
        setShouldRender(true);
      }
    }, [isVisible, shouldRender]);

    useEffect(() => {
      if (shouldRender && videoRef.current && onLoad) {
        onLoad(index, videoId, videoRef, progressRef, progressBarRef);
      }
    }, [shouldRender, index, videoId, onLoad]);

    return (
      <div className="short-item" data-index={index}>
        <div style={{ position: 'relative', width: '100%', maxWidth: '480px' }}>
          {shouldRender ? (
            <>
              <iframe
                ref={videoRef}
                className="short-iframe"
                src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&controls=0&playsinline=1`}
                allow="autoplay; fullscreen"
                title={`short-${videoId}`}
                loading="lazy"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}
              />
              <div
                className="shorts-progress-container"
                ref={progressRef}
                onClick={(e) => onProgressClick(e, index)}
                onMouseDown={(e) => onProgressDragStart(e, index)}
                onTouchStart={(e) => onProgressDragStart(e, index)}
              >
                <div
                  className="shorts-progress-bar"
                  ref={progressBarRef}
                  style={{ width: '0%' }}
                >
                  <div className="shorts-progress-thumb" />
                </div>
              </div>
            </>
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                alt="Short thumbnail"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
  (prev, next) => 
    prev.videoId === next.videoId && 
    prev.index === next.index && 
    prev.isVisible === next.isVisible
);

/* ──────────────────────  YOUTUBE API LOADER  ────────────────────── */
let ytApiLoaded = false;
let ytLoadCallbacks = [];

const loadYouTubeAPI = () => {
  return new Promise((resolve) => {
    if (ytApiLoaded && window.YT?.Player) {
      resolve();
      return;
    }

    if (ytLoadCallbacks.length === 0) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.id = 'yt-api-script';
      document.body.appendChild(script);

      window.onYouTubeIframeAPIReady = () => {
        ytApiLoaded = true;
        ytLoadCallbacks.forEach((cb) => cb());
        ytLoadCallbacks = [];
      };
    }

    ytLoadCallbacks.push(resolve);
  });
};

/* ──────────────────────  MAIN COMPONENT  ────────────────────── */
const Latest = () => {
  const [videos, setVideos] = useState([]);
  const [loadedVideos, setLoadedVideos] = useState(3);
  const [isDragging, setIsDragging] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTopicBox, setShowTopicBox] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [visibleIndices, setVisibleIndices] = useState(new Set([0, 1, 2]));

  const videoRefs = useRef({});
  const playerRefs = useRef({});
  const progressRefs = useRef({});
  const progressBarRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const scrollRestored = useRef(false);
  const watchedVideosBuffer = useRef(new Set());
  const observerRef = useRef(null);
  const isInitialMount = useRef(true);
  const currentPlayingIndex = useRef(null);
  const playersReadyRef = useRef(new Set());
  const [apiReady, setApiReady] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const topics = ['Tech', 'AI', 'Science', 'Geopolitics', 'Business'];
  const userLanguage = localStorage.getItem('userLanguage') || 'English';
  const savedCategories = (() => {
    try {
      const s = localStorage.getItem('selectedCategories');
      return s ? JSON.parse(s) : ['all'];
    } catch {
      return ['all'];
    }
  })();
  const initialCategories = location.state?.categories || savedCategories;

  /* ──────  LOAD YOUTUBE API ONCE  ────── */
  useEffect(() => {
    loadYouTubeAPI().then(() => setApiReady(true));
  }, []);

  /* ──────  TOPIC SELECTION  ────── */
  useEffect(() => {
    try {
      const s = localStorage.getItem('selectedCategories');
      if (s) {
        const p = JSON.parse(s);
        if (Array.isArray(p) && !p.includes('all')) setSelectedTopics(p);
      }
    } catch {}
  }, []);

  const toggleTopic = (t) =>
    setSelectedTopics((prev) => {
      const has = prev.includes(t);
      return has ? prev.filter((x) => x !== t) : [...prev, t];
    });

  const handleSetTopics = () => {
    const cats = selectedTopics.length ? selectedTopics : ['all'];
    localStorage.setItem('selectedCategories', JSON.stringify(cats));
    setShowTopicBox(false);
    navigate('/latest', { state: { categories: cats }, replace: true });
  };

  const toggleTopicBox = () => setShowTopicBox((p) => !p);
  const goHome = () => navigate('/videos');
  const goFeedback = () => navigate('/feedback');

  /* ──────  HELPERS  ────── */
  const shuffleArray = useCallback((arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }, []);

  const getWatchedVideos = useCallback(() => {
    try {
      const w = localStorage.getItem('watchedShorts');
      return w ? JSON.parse(w) : [];
    } catch {
      return [];
    }
  }, []);

  const saveWatchedVideos = useCallback(() => {
    if (watchedVideosBuffer.current.size) {
      try {
        const cur = getWatchedVideos();
        const merged = [...new Set([...cur, ...watchedVideosBuffer.current])];
        localStorage.setItem('watchedShorts', JSON.stringify(merged));
        watchedVideosBuffer.current.clear();
      } catch {}
    }
  }, [getWatchedVideos]);

  const resetWatchedVideos = useCallback(() => {
    try {
      localStorage.removeItem('watchedShorts');
      watchedVideosBuffer.current.clear();
    } catch {}
  }, []);

  const saveScrollPosition = useCallback(() => {
    if (scrollRestored.current && scrollContainerRef.current) {
      try {
        const pos = scrollContainerRef.current.scrollTop;
        localStorage.setItem('shortsScrollPosition', String(pos));
        localStorage.setItem('shortsVisibleIndex', String(currentIndex));
      } catch {}
    }
  }, [currentIndex]);

  const getScrollPosition = useCallback(() => {
    try {
      const p = localStorage.getItem('shortsScrollPosition');
      const i = localStorage.getItem('shortsVisibleIndex');
      return { position: p ? Number(p) : 0, index: i ? Number(i) : 0 };
    } catch {
      return { position: 0, index: 0 };
    }
  }, []);

  const throttledSaveScroll = useMemo(
    () => throttle(saveScrollPosition, 500),
    [saveScrollPosition]
  );

  /* ──────  PLAYER CONTROLS  ────── */
  const pauseVideo = useCallback((idx) => {
    const p = playerRefs.current[idx];
    if (!p || !playersReadyRef.current.has(idx)) return;
    try {
      const s = p.getPlayerState?.();
      if (s === window.YT?.PlayerState?.PLAYING) p.pauseVideo();
    } catch {}
  }, []);

  const playVideo = useCallback((idx) => {
    const p = playerRefs.current[idx];
    if (!p || !playersReadyRef.current.has(idx)) return;
    try {
      p.unMute?.();
      p.playVideo();
    } catch {}
  }, []);

  const cleanupDistant = useCallback((cur) => {
    const newVisible = new Set();
    
    // Keep current +/- 2 for smooth experience
    for (let i = Math.max(0, cur - 2); i <= Math.min(videos.length - 1, cur + 2); i++) {
      newVisible.add(i);
    }
    
    setVisibleIndices(newVisible);
    
    // Clean up players that are far away (more than 3 positions)
    Object.keys(playerRefs.current).forEach((k) => {
      const i = Number(k);
      if (Math.abs(i - cur) > 3 && playerRefs.current[i]) {
        try { playerRefs.current[i].destroy(); } catch {}
        delete playerRefs.current[i];
        delete videoRefs.current[i];
        delete progressRefs.current[i];
        delete progressBarRefs.current[i];
        playersReadyRef.current.delete(i);
      }
    });
  }, [videos.length]);

  const stopProgress = useCallback(() => {
    if (progressIntervalRef.current) {
      cancelAnimationFrame(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const startProgress = useCallback(
    (idx, vid) => {
      stopProgress();
      const p = playerRefs.current[idx];
      if (!p?.getDuration) return;

      const tick = () => {
        if (document.hidden || currentPlayingIndex.current !== idx) {
          stopProgress();
          return;
        }

        const pl = playerRefs.current[idx];
        if (!pl) {
          stopProgress();
          return;
        }

        try {
          const state = pl.getPlayerState?.();
          if (state !== window.YT?.PlayerState?.PLAYING) return;

          const dur = pl.getDuration() || 0;
          const cur = pl.getCurrentTime() || 0;
          if (!dur || isNaN(dur)) return;

          const pct = Math.min((cur / dur) * 100, 100);
          const bar = progressBarRefs.current[idx];
          if (bar) {
            bar.style.transition = 'width 0.08s linear';
            bar.style.width = `${pct}%`;
          }
          if (pct >= 90 && !watchedVideosBuffer.current.has(vid))
            watchedVideosBuffer.current.add(vid);

          progressIntervalRef.current = requestAnimationFrame(tick);
        } catch {
          stopProgress();
        }
      };
      progressIntervalRef.current = requestAnimationFrame(tick);
    },
    [stopProgress]
  );

  /* ──────  PLAYER INITIALISATION  ────── */
  const initPlayer = useCallback(
    (idx, vid, vRef, pRef, pbRef) => {
      // Don't initialize if already exists or not visible
      if (playerRefs.current[idx] || !visibleIndices.has(idx)) return;
      
      if (!window.YT?.Player || !apiReady) {
        console.warn('YT API not ready, retrying...');
        setTimeout(() => initPlayer(idx, vid, vRef, pRef, pbRef), 300);
        return;
      }

      // Verify refs exist before proceeding
      if (!vRef.current || !pRef.current || !pbRef.current) {
        console.warn('Refs not ready for player', idx);
        return;
      }

      videoRefs.current[idx] = vRef.current;
      progressRefs.current[idx] = pRef.current;
      progressBarRefs.current[idx] = pbRef.current;

      try {
        playerRefs.current[idx] = new window.YT.Player(vRef.current, {
          videoId: vid,
          playerVars: {
            autoplay: 1,
            mute: 0,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
            fs: 1,
            iv_load_policy: 3,
            enablejsapi: 1,
          },
          events: {
            onReady: () => {
              playersReadyRef.current.add(idx);
              if (pbRef.current) pbRef.current.style.width = '0%';

              if (idx === currentPlayingIndex.current) {
                playVideo(idx);
                startProgress(idx, vid);
              } else if (currentPlayingIndex.current === null && idx === 0) {
                currentPlayingIndex.current = 0;
                playVideo(0);
                startProgress(0, vid);
              }
            },
            onStateChange: (e) => {
              try {
                if (e.data === window.YT?.PlayerState?.ENDED) {
                  watchedVideosBuffer.current.add(vid);
                  stopProgress();
                  e.target.playVideo();
                  if (pbRef.current) pbRef.current.style.width = '0%';
                } else if (e.data === window.YT?.PlayerState?.PLAYING) {
                  if (currentPlayingIndex.current === idx) {
                    startProgress(idx, vid);
                    e.target.unMute();
                  }
                } else if (e.data === window.YT?.PlayerState?.PAUSED) {
                  stopProgress();
                }
              } catch (err) {
                console.error('State change error:', err);
              }
            },
            onError: (e) => console.error(`YT error ${vid}: ${e.data}`),
          },
        });
      } catch (err) {
        console.error('Failed to create player:', err);
        delete playerRefs.current[idx];
      }
    },
    [playVideo, startProgress, stopProgress, visibleIndices, apiReady]
  );

  const onIframeLoad = useCallback(
    (idx, vid, vRef, pRef, pbRef) => {
      setTimeout(() => initPlayer(idx, vid, vRef, pRef, pbRef), 400);
    },
    [initPlayer]
  );

  /* ──────  INTERSECTION OBSERVER  ────── */
  useEffect(() => {
    if (!videos.length || !apiReady || !scrollContainerRef.current) return;
    
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    try {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const idx = Number(entry.target.getAttribute('data-index'));
            if (isNaN(idx) || !playersReadyRef.current.has(idx)) return;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.75) {
              if (currentPlayingIndex.current === idx) return;

              if (currentPlayingIndex.current !== null) {
                pauseVideo(currentPlayingIndex.current);
                stopProgress();
              }

              setCurrentIndex(idx);
              currentPlayingIndex.current = idx;
              playVideo(idx);
              startProgress(idx, videos[idx]);
              cleanupDistant(idx);
              throttledSaveScroll();
            } else {
              pauseVideo(idx);
              if (currentPlayingIndex.current === idx) stopProgress();
            }
          });
        },
        { threshold: 0.75, root: scrollContainerRef.current }
      );

      const items = scrollContainerRef.current?.querySelectorAll('.short-item') ?? [];
      items.forEach((it) => {
        if (observerRef.current && it) {
          observerRef.current.observe(it);
        }
      });
    } catch (err) {
      console.error('Observer setup error:', err);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [
    videos,
    loadedVideos,
    apiReady,
    pauseVideo,
    playVideo,
    startProgress,
    stopProgress,
    cleanupDistant,
    throttledSaveScroll,
  ]);

  /* ──────  FETCH  ────── */
  const fetchShorts = useCallback(async () => {
    setIsFetching(true);
    setError(null);
    setVideos([]);
    setLoadedVideos(3);
    setCurrentIndex(0);
    setVisibleIndices(new Set([0, 1, 2]));
    currentPlayingIndex.current = null;
    scrollRestored.current = false;
    playersReadyRef.current.clear();

    stopProgress();
    Object.keys(playerRefs.current).forEach((k) => {
      try { playerRefs.current[Number(k)]?.destroy(); } catch {}
    });
    playerRefs.current = {};
    videoRefs.current = {};
    progressRefs.current = {};
    progressBarRefs.current = {};

    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    localStorage.removeItem('shortsScrollPosition');
    localStorage.removeItem('shortsVisibleIndex');

    try {
      const lang = userLanguage.charAt(0).toUpperCase() + userLanguage.slice(1).toLowerCase();
      const resp = await fetch(`/API/Latest/${lang}Short.json`, {
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const txt = await resp.text();
      if (!txt) throw new Error('empty');
      const data = JSON.parse(txt);

      const norm = {};
      Object.keys(data).forEach((k) => {
        norm[k.toLowerCase()] = Array.isArray(data[k]) ? data[k] : [];
      });

      const cats = initialCategories.map((c) => c.toLowerCase());
      const watched = getWatchedVideos();

      let list = [];
      if (cats.includes('all')) {
        Object.values(norm).forEach((a) => list.push(...a));
      } else {
        cats.forEach((c) => norm[c] && list.push(...norm[c]));
      }

      list = list.filter((id) => id && typeof id === 'string' && !watched.includes(id));

      if (!list.length) {
        resetWatchedVideos();
        if (cats.includes('all')) {
          Object.values(norm).forEach((a) => list.push(...a));
        } else {
          cats.forEach((c) => norm[c] && list.push(...norm[c]));
        }
      }

      if (!list.length) setError('No shorts available');
      else setVideos(shuffleArray(list));
    } catch (e) {
      console.error('[fetch]', e);
      setError(e.message || 'Failed to load');
    } finally {
      setIsFetching(false);
    }
  }, [
    userLanguage,
    initialCategories,
    shuffleArray,
    getWatchedVideos,
    resetWatchedVideos,
    stopProgress,
  ]);

  const debouncedFetch = useMemo(() => debounce(fetchShorts, 200), [fetchShorts]);

  useEffect(() => {
    fetchShorts();
    const onStorage = (e) => {
      if (e.key === 'userLanguage' || e.key === 'selectedCategories') debouncedFetch();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('vitstra-update', debouncedFetch);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('vitstra-update', debouncedFetch);
      stopProgress();
      saveWatchedVideos();
      Object.keys(playerRefs.current).forEach((k) => {
        try { playerRefs.current[Number(k)]?.destroy(); } catch {}
      });
    };
  }, [fetchShorts, debouncedFetch, saveWatchedVideos, stopProgress]);

  /* ──────  SCROLL RESTORE  ────── */
  useEffect(() => {
    if (!videos.length || !isInitialMount.current || scrollRestored.current) return;
    isInitialMount.current = false;
    const { position, index } = getScrollPosition();
    if (index > 0 && index < videos.length && index < loadedVideos) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = position;
          setCurrentIndex(index);
          currentPlayingIndex.current = index;
          scrollRestored.current = true;
          if (playersReadyRef.current.has(index) && apiReady) {
            playVideo(index);
            startProgress(index, videos[index]);
          }
        }
      }, 500);
    } else scrollRestored.current = true;
  }, [videos, loadedVideos, getScrollPosition, playVideo, startProgress, apiReady]);

  /* ──────  VISIBILITY / UNLOAD  ────── */
  useEffect(() => {
    const onVis = () => {
      try {
        if (document.hidden) {
          Object.keys(playerRefs.current).forEach((k) => pauseVideo(Number(k)));
          stopProgress();
          throttledSaveScroll();
          saveWatchedVideos();
        } else if (currentPlayingIndex.current !== null && videos.length > 0) {
          playVideo(currentPlayingIndex.current);
          startProgress(currentPlayingIndex.current, videos[currentPlayingIndex.current]);
        }
      } catch (err) {
        console.error('Visibility change error:', err);
      }
    };
    
    const onBeforeUnload = () => {
      try {
        saveScrollPosition();
        saveWatchedVideos();
      } catch (err) {
        console.error('Unload error:', err);
      }
    };
    
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('beforeunload', onBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [
    currentIndex,
    videos,
    pauseVideo,
    playVideo,
    startProgress,
    stopProgress,
    throttledSaveScroll,
    saveScrollPosition,
    saveWatchedVideos,
  ]);

  /* ──────  SCROLL HANDLER  ────── */
  const onScroll = useCallback(
    (e) => {
      const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        setLoadedVideos((p) => Math.min(p + 1, videos.length));
      }
      throttledSaveScroll();
    },
    [throttledSaveScroll, videos.length]
  );

  /* ──────  SEEKING  ────── */
  const seek = useCallback((e, idx) => {
    try {
      const p = playerRefs.current[idx];
      const bar = progressRefs.current[idx];
      if (!p || !bar || !p.getDuration) return;
      
      const rect = bar.getBoundingClientRect();
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const pct = Math.min(Math.max((x - rect.left) / rect.width, 0), 1);
      
      const duration = p.getDuration();
      if (!duration || isNaN(duration)) return;
      
      p.seekTo(duration * pct, true);
      const prog = progressBarRefs.current[idx];
      if (prog) {
        prog.style.transition = 'none';
        prog.style.width = `${pct * 100}%`;
      }
    } catch (err) {
      console.error('Seek error:', err);
    }
  }, []);

  const onProgressClick = useCallback((e, i) => { e.stopPropagation(); seek(e, i); }, [seek]);
  const onDragStart = useCallback((e, i) => { e.preventDefault(); setIsDragging(i); pauseVideo(i); seek(e, i); }, [seek, pauseVideo]);
  const onDragMove = useCallback((e) => { if (isDragging !== null) seek(e, isDragging); }, [isDragging, seek]);
  const onDragEnd = useCallback(() => {
    if (isDragging !== null) {
      const b = progressBarRefs.current[isDragging];
      if (b) b.style.transition = 'width 0.08s linear';
      playVideo(isDragging);
      setIsDragging(null);
    }
  }, [isDragging, playVideo]);

  useEffect(() => {
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchend', onDragEnd);
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchend', onDragEnd);
    };
  }, [onDragMove, onDragEnd]);

  const scrollUp = useCallback(() => {
    if (scrollContainerRef.current && currentIndex > 0) {
      const items = scrollContainerRef.current.querySelectorAll('.short-item');
      items[currentIndex - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentIndex]);

  const scrollDown = useCallback(() => {
    if (scrollContainerRef.current && currentIndex < videos.length - 1) {
      const items = scrollContainerRef.current.querySelectorAll('.short-item');
      items[currentIndex + 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentIndex, videos.length]);

  /* ──────  RENDER  ────── */
  if (isFetching) {
    return (
      <div className="shorts-container">
        <div className="loading-indicator"><h2>Loading shorts...</h2></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shorts-container">
        <button className="change-topics-button" onClick={toggleTopicBox}>Settings</button>
        <div className="error-message"><h2>Error</h2><p>{error}</p></div>
      </div>
    );
  }

  if (!videos.length) {
    return (
      <div className="shorts-container">
        <button className="change-topics-button" onClick={toggleTopicBox}>Settings</button>
        <div className="no-shorts-message">
          <h2>No shorts available!</h2>
          <p>Try selecting different topics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shorts-container" ref={scrollContainerRef} onScroll={onScroll}>
      <button className="change-topics-button" onClick={toggleTopicBox}>✵</button>

      {showTopicBox && (
        <div className="topic-selection-box" key="topic-box">
          <h3>Choose Your Interests</h3>
          <div className="top-topic-buttons">
            {topics.map((t) => (
              <button
                key={t}
                className={`top-topic-btn ${selectedTopics.includes(t) ? 'selected' : ''}`}
                onClick={() => toggleTopic(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="topic-box-actions">
            <button className="set-btn" onClick={handleSetTopics}>Set</button>
            <button className="feedback-btn" onClick={goFeedback}>Feedback</button>
          </div>
        </div>
      )}

      <button onClick={goHome} className="back-btn">←</button>

      {!apiReady ? (
        <div className="loading-message">Loading YouTube Player...</div>
      ) : (
        videos.slice(0, loadedVideos).map((vid, i) => (
          <ShortItem
            key={`${vid}-${i}`}
            videoId={vid}
            index={i}
            isVisible={visibleIndices.has(i)}
            onLoad={onIframeLoad}
            onProgressClick={onProgressClick}
            onProgressDragStart={onDragStart}
          />
        ))
      )}

      <button className="scroll-button scroll-up" onClick={scrollUp}>↑</button>
      <button className="scroll-button scroll-down" onClick={scrollDown}>↓</button>
    </div>
  );
};

export default Latest;